import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import Panel from './Panel';
import FilterControls from './FilterControls';
import { usePartyColors } from '../contexts/PartyColorContext';
import { 
  extractUniqueHashtags, 
  extractUniqueSpeakers, 
  applyAllFilters, 
  getResultCounts 
} from '../utils/filterUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);


const Dashboard = () => {
  const { getPartyColor } = usePartyColors();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allData, setAllData] = useState({
    meetings: [],
    agendaItems: [],
    speeches: []
  });
  const [filteredData, setFilteredData] = useState({
    meetings: [],
    agendaItems: [],
    speeches: []
  });
  const [dashboardData, setDashboardData] = useState({
    politicalLeaning: { left: 0, center: 0, right: 0 },
    politicalLeaningGradient: [],
    topHashtags: [],
    topSpeakers: [],
    recentActivity: []
  });
  const [filterOptions, setFilterOptions] = useState({
    hashtags: [],
    speakers: []
  });
  const [isGradientView, setIsGradientView] = useState(true); // Default to gradient view
  
  // Initialize filters with default last month
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const [filters, setFilters] = useState({
    dateRange: getDefaultDateRange(),
    selectedHashtags: [],
    selectedSpeakers: []
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (allData.meetings.length > 0) {
      applyFiltersAndUpdateDashboard();
    }
  }, [filters, allData]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch meetings
      const { data: meetings, error: meetingsError } = await supabase
        .from('meetings')
        .select('*')
        .order('date_of_sitting', { ascending: false });

      if (meetingsError) throw meetingsError;

      // Fetch agenda items with leaning scores
      const { data: agendaItems, error: agendaError } = await supabase
        .from('agenda_items')
        .select('*');

      if (agendaError) throw agendaError;

      // Fetch speeches
      const { data: speeches, error: speechesError } = await supabase
        .from('speeches')
        .select('*')
        .order('time', { ascending: false });

      if (speechesError) throw speechesError;

      const data = {
        meetings: meetings || [],
        agendaItems: agendaItems || [],
        speeches: speeches || []
      };

      setAllData(data);

      // Set filter options
      setFilterOptions({
        hashtags: extractUniqueHashtags(data.meetings, data.agendaItems),
        speakers: extractUniqueSpeakers(data.speeches)
      });
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndUpdateDashboard = () => {
    const filtered = applyAllFilters(
      allData.meetings,
      allData.agendaItems,
      allData.speeches,
      filters
    );
    
    setFilteredData(filtered);
    
    // Process dashboard data
    const processed = processDashboardData(filtered);
    setDashboardData(processed);
  };

  // Color interpolation function for gradient view
  const getGradientColor = (score) => {
    // Clamp score to [-2, 2] range
    const clampedScore = Math.max(-2, Math.min(2, score));
    
    // Normalize score from [-2, 2] to [0, 1]
    const normalized = (clampedScore + 2) / 4;
    
    // Define color transitions: Red -> Purple -> Blue
    let r, g, b;
    
    if (normalized <= 0.5) {
      // Red to Purple transition (0 to 0.5)
      const t = normalized * 2; // Scale to [0, 1]
      r = Math.round(239 * (1 - t) + 147 * t); // Red to Purple red component
      g = Math.round(68 * (1 - t) + 51 * t);   // Red to Purple green component
      b = Math.round(68 * (1 - t) + 234 * t);  // Red to Purple blue component
    } else {
      // Purple to Blue transition (0.5 to 1)
      const t = (normalized - 0.5) * 2; // Scale to [0, 1]
      r = Math.round(147 * (1 - t) + 59 * t);  // Purple to Blue red component
      g = Math.round(51 * (1 - t) + 130 * t);  // Purple to Blue green component
      b = Math.round(234 * (1 - t) + 246 * t); // Purple to Blue blue component
    }
    
    return `rgba(${r}, ${g}, ${b}, 0.8)`;
  };

  const processDashboardData = (data) => {
    // Process political leaning distribution (categorical)
    const politicalLeaning = { left: 0, center: 0, right: 0 };
    
    // Process gradient data
    const scoreRanges = {};
    const validScores = [];
    
    data.agendaItems.forEach(item => {
      if (item.leaning_score !== null && item.leaning_score !== undefined) {
        validScores.push(item.leaning_score);
        
        // Categorical counting
        if (item.leaning_score <= -0.5) {
          politicalLeaning.left++;
        } else if (item.leaning_score >= 0.5) {
          politicalLeaning.right++;
        } else {
          politicalLeaning.center++;
        }
        
        // Gradient grouping (group by 0.2 increments for smoother visualization)
        const rangeKey = Math.round(item.leaning_score * 5) / 5; // Round to nearest 0.2
        scoreRanges[rangeKey] = (scoreRanges[rangeKey] || 0) + 1;
      }
    });

    // Create gradient data
    const politicalLeaningGradient = Object.entries(scoreRanges)
      .map(([score, count]) => ({
        score: parseFloat(score),
        count,
        color: getGradientColor(parseFloat(score)),
        label: `Score: ${parseFloat(score).toFixed(1)}`
      }))
      .sort((a, b) => a.score - b.score);

    // Process hashtag data (only from agenda items)
    const hashtagCounts = {};
    const allHashtags = [];

    // Process agenda item hashtags only
    data.agendaItems.forEach(item => {
      if (item.hashtags && Array.isArray(item.hashtags)) {
        item.hashtags.forEach(hashtag => {
          hashtagCounts[hashtag] = (hashtagCounts[hashtag] || 0) + 1;
          allHashtags.push({ hashtag, type: 'agenda' });
        });
      }
    });

    const topHashtags = Object.entries(hashtagCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([hashtag, count]) => ({ hashtag, count }));

    // Process speaker data (now uses filtered speeches to match filter criteria)
    const speakerCounts = {};
    const speakerParties = {};

    data.speeches.forEach(speech => {
      const speakerName = `${speech.speaker_first_name} ${speech.speaker_last_name}`;
      speakerCounts[speakerName] = (speakerCounts[speakerName] || 0) + 1;
      if (speech.speaker_party) {
        speakerParties[speakerName] = speech.speaker_party;
      }
    });

    const topSpeakers = Object.entries(speakerCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([speaker, count]) => ({ 
        speaker, 
        count, 
        party: speakerParties[speaker] || 'Unknown'
      }));

    return {
      politicalLeaning,
      politicalLeaningGradient,
      topHashtags,
      topSpeakers,
      recentActivity: allHashtags.slice(0, 15)
    };
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: getDefaultDateRange(),
      selectedHashtags: [],
      selectedSpeakers: []
    });
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // Political Leaning Chart (Categorical)
  const politicalLeaningChart = {
    labels: ['Left Leaning', 'Center Leaning', 'Right Leaning'],
    datasets: [
      {
        data: [
          dashboardData.politicalLeaning.left,
          dashboardData.politicalLeaning.center,
          dashboardData.politicalLeaning.right
        ],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)', // Red for left
          'rgba(147, 51, 234, 0.8)', // Purple for center
          'rgba(59, 130, 246, 0.8)', // Blue for right
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(147, 51, 234, 1)',
          'rgba(59, 130, 246, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Political Leaning Chart (Gradient)
  const politicalLeaningGradientChart = {
    labels: dashboardData.politicalLeaningGradient.map(item => item.label),
    datasets: [
      {
        data: dashboardData.politicalLeaningGradient.map(item => item.count),
        backgroundColor: dashboardData.politicalLeaningGradient.map(item => item.color),
        borderColor: dashboardData.politicalLeaningGradient.map(item => item.color.replace('0.8', '1')),
        borderWidth: 1,
      },
    ],
  };

  // Top Hashtags Chart
  const topHashtagsChart = {
    labels: dashboardData.topHashtags.map(item => `${item.hashtag}`),
    datasets: [
      {
        label: 'Usage Count',
        data: dashboardData.topHashtags.map(item => item.count),
        backgroundColor: 'rgba(79, 70, 229, 0.8)',
        borderColor: 'rgba(79, 70, 229, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Top Speakers Chart with party colors

  const topSpeakersChart = {
    labels: dashboardData.topSpeakers.map(item => item.speaker),
    datasets: [
      {
        label: 'Speech Count',
        data: dashboardData.topSpeakers.map(item => item.count),
        backgroundColor: dashboardData.topSpeakers.map(item => getPartyColor(item.party)),
        borderColor: dashboardData.topSpeakers.map(item => getPartyColor(item.party)),
        borderWidth: 1,
      },
    ],
  };

  const horizontalChartOptions = {
    ...chartOptions,
    indexAxis: 'y',
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        callbacks: {
          afterLabel: function(context) {
            const speaker = dashboardData.topSpeakers[context.dataIndex];
            return `Party: ${speaker.party}`;
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center transition-colors duration-200">
        <LoadingSpinner message="Loading dashboard data..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-200">
      <main className="container mx-auto p-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Political Analytics Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-300">Insights into political leaning, hashtag usage, and speaker activity</p>
        </div>

        <ErrorMessage message={error} />

        {/* Filter Controls */}
        <FilterControls
          filters={filters}
          onFiltersChange={handleFiltersChange}
          hashtagOptions={filterOptions.hashtags}
          speakerOptions={filterOptions.speakers}
          onClearFilters={handleClearFilters}
          resultCounts={getResultCounts(filteredData)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Political Leaning Distribution */}
          <Panel 
            title="Political Leaning Distribution"
            headerContent={
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsGradientView(!isGradientView)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    isGradientView
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }`}
                >
                  {isGradientView ? 'Switch to Categories' : 'Switch to Gradient'}
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {isGradientView ? 'Detailed scores' : 'Simple categories'}
                </span>
              </div>
            }
          >
            <div className="h-80">
              <Doughnut 
                data={isGradientView ? politicalLeaningGradientChart : politicalLeaningChart} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      display: !isGradientView || dashboardData.politicalLeaningGradient.length <= 8,
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const total = context.dataset.data.reduce((a, b) => a + b, 0);
                          const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                          
                          if (isGradientView) {
                            const gradientItem = dashboardData.politicalLeaningGradient[context.dataIndex];
                            return `${gradientItem.label}: ${context.parsed} items (${percentage}%)`;
                          } else {
                            return `${context.label}: ${context.parsed} (${percentage}%)`;
                          }
                        }
                      }
                    }
                  },
                }} 
              />
            </div>
            {isGradientView && (
              <div className="mt-4 text-xs text-gray-600 dark:text-gray-400">
                <div className="flex items-center justify-between">
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 rounded mr-1"></div>
                    Left (-2.0)
                  </span>
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded mr-1"></div>
                    Center (0.0)
                  </span>
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded mr-1"></div>
                    Right (+2.0)
                  </span>
                </div>
              </div>
            )}
          </Panel>

          {/* Top Hashtags Chart */}
          <Panel title="Most Popular Hashtags">
            <div className="h-80">
              <Bar data={topHashtagsChart} options={chartOptions} />
            </div>
          </Panel>

          {/* Top Speakers Chart */}
          <Panel title="Top 10 Speakers (Filtered)">
            <div className="h-80">
              <Bar data={topSpeakersChart} options={horizontalChartOptions} />
            </div>
          </Panel>

          {/* Recent Activity */}
          <Panel title="Recent Hashtag Activity">
            <div className="h-80 overflow-y-auto">
              <div className="space-y-3">
                {dashboardData.recentActivity.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20">
                      {item.hashtag}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {item.type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
